import React from "react";
import { observer } from "mobx-react";
import {
  Box,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  useTheme,
} from "@mui/material";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import { PodRevenueDashboardStore } from "../../../../stores/dashboards/PodRevenueDashboardStore";
import { PodRevenueSortBy } from "../../../../../../models/Dashboard/PodRevenue/PodRevenueFilters";
import {
  formatPodCount,
  formatPodMoney,
  formatPodShare,
} from "./podRevenueFormat";
import {
  PodRevenueEmptyState,
  PodRevenueSectionLabel,
  podHeaderCellSx,
} from "./PodRevenueSection";
import PodRevenuePager, { PodRevenueTableProgress } from "./PodRevenuePager";

interface Props {
  store: PodRevenueDashboardStore;
}

const cols: Array<{
  id: string;
  label: string;
  sort?: PodRevenueSortBy;
  align?: "left" | "center" | "right";
}> = [
  { id: "product", label: "Product", align: "left" },
  { id: "orders", label: "Orders", sort: "orders", align: "center" },
  { id: "units", label: "Units", sort: "units", align: "center" },
  {
    id: "selling_exclusive",
    label: "Selling excl.",
    sort: "selling_exclusive",
    align: "center",
  },
  { id: "gst", label: "GST", sort: "gst", align: "center" },
  { id: "handling", label: "Handling", sort: "handling", align: "center" },
  { id: "shipping", label: "Shipping", sort: "shipping", align: "center" },
  { id: "gross_revenue", label: "Gross", sort: "gross_revenue", align: "center" },
  { id: "share", label: "Share", align: "center" },
  { id: "go", label: "", align: "center" },
];

const num = { fontVariantNumeric: "tabular-nums" as const };

const PodRevenueProductsTable: React.FC<Props> = observer(({ store }) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const totalGross = store.summary?.data.kpis.gross_revenue || 0;
  const selectedId = store.drawerOpen ? store.drawerProduct?.product_id : null;
  const cellSx = { borderColor: colors.lightGray };
  const showSkeleton = store.productsLoading && store.products.length === 0;

  return (
    <>
      <PodRevenueSectionLabel
        label="Top products"
        hint="Click a product to open its order lines."
        action={
          <CustomTypography
            variant="heading8"
            fontWeight={500}
            color={colors.mutedSlateText}
          >
            {formatPodCount(store.productsTotal)} products
          </CustomTypography>
        }
      />
      {store.productsError ? (
        <PodRevenueEmptyState
          error
          message={store.productsError}
          onRetry={() => void store.fetchProducts()}
        />
      ) : store.products.length === 0 && !store.productsLoading ? (
        <PodRevenueEmptyState message="No products found for the selected filters." />
      ) : (
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 2,
            borderColor: colors.lightGray,
            overflow: "hidden",
          }}
        >
          <PodRevenueTableProgress
            loading={store.productsLoading && !showSkeleton}
          />
          <TableContainer
            sx={{
              maxHeight: 440,
              opacity: store.productsLoading && !showSkeleton ? 0.6 : 1,
              transition: "opacity .15s ease",
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {cols.map((col) => (
                    <TableCell
                      key={col.id}
                      align={col.align ?? "left"}
                      sx={{
                        ...podHeaderCellSx,
                        color: colors.greyTextColor,
                        bgcolor: colors.white,
                        borderColor: colors.lightGray,
                      }}
                    >
                      {col.sort ? (
                        <TableSortLabel
                          active={store.productsSortBy === col.sort}
                          direction={
                            store.productsSortBy === col.sort &&
                            store.productsSortType === "ASC"
                              ? "asc"
                              : "desc"
                          }
                          onClick={() =>
                            void store.setProductsSort(
                              col.sort as PodRevenueSortBy,
                            )
                          }
                          sx={
                            col.align === "center"
                              ? {
                                  justifyContent: "center",
                                  width: "100%",
                                  position: "relative",
                                  "& .MuiTableSortLabel-icon": {
                                    position: "absolute",
                                    right: 0,
                                  },
                                }
                              : undefined
                          }
                        >
                          {col.label}
                        </TableSortLabel>
                      ) : (
                        col.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {showSkeleton
                  ? Array.from({ length: 5 }).map((_, i) => (
                      <TableRow key={`sk-${i}`}>
                        {cols.map((c) => (
                          <TableCell key={c.id} align={c.align ?? "left"} sx={cellSx}>
                            <Skeleton variant="text" />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  : store.products.map((row) => {
                      const selected = selectedId === row.product_id;
                      const sharePct =
                        totalGross > 0
                          ? Math.min(
                              100,
                              (row.gross_revenue / totalGross) * 100,
                            )
                          : 0;
                      const open = () => void store.openProduct(row);
                      return (
                        <TableRow
                          key={row.product_id}
                          hover
                          selected={selected}
                          tabIndex={0}
                          onClick={open}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              open();
                            }
                          }}
                          sx={{
                            cursor: "pointer",
                            transition: "background-color .12s ease",
                            "& > td:first-of-type": {
                              boxShadow: selected
                                ? `inset 3px 0 0 ${colors.darkBlue}`
                                : "none",
                            },
                            "&.Mui-selected, &.Mui-selected:hover": {
                              bgcolor: colors.lightBlue,
                            },
                            "&:hover .pod-row-go": {
                              opacity: 1,
                              transform: "translateX(0)",
                            },
                            "&:focus-visible": {
                              outline: `2px solid ${colors.darkBlue}`,
                              outlineOffset: -2,
                            },
                          }}
                        >
                          <TableCell sx={cellSx}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.25,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 34,
                                  height: 34,
                                  borderRadius: 1,
                                  display: "grid",
                                  placeItems: "center",
                                  bgcolor: selected
                                    ? colors.white
                                    : colors.veryLightGray,
                                  color: selected
                                    ? colors.darkBlue
                                    : colors.greyTextColor,
                                  flexShrink: 0,
                                }}
                              >
                                <Inventory2OutlinedIcon sx={{ fontSize: 17 }} />
                              </Box>
                              <Box minWidth={0}>
                                <CustomTypography
                                  variant="heading7"
                                  fontWeight={600}
                                  color={colors.navyText}
                                >
                                  {row.product_name ||
                                    `Product #${row.product_id}`}
                                </CustomTypography>
                                <CustomTypography
                                  variant="heading8"
                                  color={colors.mutedSlateText}
                                >
                                  {row.sku || "—"} · #{row.product_id}
                                </CustomTypography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell align="center" sx={{ ...cellSx, ...num }}>
                            {formatPodCount(row.order_count)}
                          </TableCell>
                          <TableCell align="center" sx={{ ...cellSx, ...num }}>
                            {formatPodCount(row.unit_count)}
                          </TableCell>
                          <TableCell align="center" sx={{ ...cellSx, ...num }}>
                            {formatPodMoney(row.selling_exclusive)}
                          </TableCell>
                          <TableCell align="center" sx={{ ...cellSx, ...num }}>
                            {formatPodMoney(row.gst_amount)}
                          </TableCell>
                          <TableCell align="center" sx={{ ...cellSx, ...num }}>
                            {formatPodMoney(row.handling_amount)}
                          </TableCell>
                          <TableCell align="center" sx={{ ...cellSx, ...num }}>
                            {formatPodMoney(row.shipping_amount)}
                          </TableCell>
                          <TableCell align="center" sx={cellSx}>
                            <CustomTypography
                              variant="heading7"
                              fontWeight={700}
                              color={colors.navyText}
                              sx={num}
                            >
                              {formatPodMoney(row.gross_revenue)}
                            </CustomTypography>
                          </TableCell>
                          <TableCell align="center" sx={{ ...cellSx, minWidth: 120 }}>
                            <Box
                              sx={{
                                display: "inline-flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <CustomTypography
                                variant="heading8"
                                color={colors.mutedSlateText}
                                sx={num}
                              >
                                {formatPodShare(row.gross_revenue, totalGross)}
                              </CustomTypography>
                              <Box
                                sx={{
                                  width: 72,
                                  height: 6,
                                  borderRadius: 999,
                                  overflow: "hidden",
                                  bgcolor: colors.greyBackground,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: `${sharePct}%`,
                                    height: "100%",
                                    borderRadius: 999,
                                    background: `linear-gradient(90deg, ${colors.purple}, ${colors.purpleDark})`,
                                  }}
                                />
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell align="center" sx={{ ...cellSx, width: 32, pl: 0 }}>
                            <ChevronRightIcon
                              className="pod-row-go"
                              sx={{
                                fontSize: 18,
                                color: colors.darkBlue,
                                opacity: selected ? 1 : 0,
                                transform: selected
                                  ? "none"
                                  : "translateX(-4px)",
                                transition: "all .15s ease",
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
              </TableBody>
            </Table>
          </TableContainer>
          <PodRevenuePager
            page={store.productsPage}
            pages={store.productsPages}
            loading={store.productsLoading}
            onPage={(p) => void store.setProductsPage(p)}
            summary={`${formatPodCount(store.productsTotal)} products`}
          />
        </Paper>
      )}
    </>
  );
});

export default PodRevenueProductsTable;
